import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Auth, Hub } from 'aws-amplify';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [loginMessage, setLoginMessage] = useState('');

  useEffect(() => {
    checkUser();

    // Listen for auth events
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signIn':
        case 'cognitoHostedUI':
          checkUser();
          break;
        case 'signOut':
          setUser(null);
          break;
        default:
          break;
      }
    });

    return () => unsubscribe();
  }, []);

  async function checkUser() {
    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      setUser(currentUser);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  const requireAuth = useCallback((message, callback) => {
    if (user) {
      callback();
    } else {
      setLoginMessage(message || 'Sign in to continue');
      setPendingAction(() => callback);
      setShowLoginModal(true);
    }
  }, [user]);

  const openLoginModal = useCallback((message) => {
    setLoginMessage(message || '');
    setPendingAction(null);
    setShowLoginModal(true);
  }, []);

  const closeLoginModal = useCallback(() => {
    setShowLoginModal(false);
    setPendingAction(null);
    setLoginMessage('');
  }, []);

  const onLoginSuccess = useCallback(() => {
    checkUser();
    setShowLoginModal(false);
    if (pendingAction) {
      // Small delay to ensure user state is updated
      setTimeout(() => {
        pendingAction();
        setPendingAction(null);
      }, 100);
    }
    setLoginMessage('');
  }, [pendingAction]);

  async function signOut() {
    try {
      await Auth.signOut();
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    requireAuth,
    signOut,
    showLoginModal,
    openLoginModal,
    closeLoginModal,
    onLoginSuccess,
    loginMessage
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
